import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { Loading } from '@bumble/app/components/Loading';
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface IamAssignEntityProps {
  iamUserId: string;
  entityName: string | undefined;
  onSubmit: () => void;
}

export const IamAssignEntity = ({ iamUserId, entityName, onSubmit }: IamAssignEntityProps) => {
  const api = useApi();

  const [entities, setEntities] = useState<string[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<string>('');

  const { isLoading, data: entityNames } = useQuery(
    ['prepareEntityNames'],
    api.getEntityNamesApiV1GetEntityNamesGet.bind(null, {}),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  useEffect(() => {
    if (entityNames != null && entityNames != undefined) {
      setEntities(entityNames.data || []);
      // No current entity means the user is at account level, so default the
      // selection to the empty ("Account Level") option rather than the first entity.
      if (entityName != null && entityName != undefined) {
        setSelectedEntity(entityName);
      } else {
        setSelectedEntity('');
      }
    }
  }, [entityNames]);

  const assignEntity = useMutation(api.iamUserAssignEntityApiV1IamUserAssignEntityPost, {
    onSuccess(data) {
      const msg = 'status' in data.data
        ? data.data.status as string
        : `Assigned entity successfully.`;
      toast.success(msg);
      onSubmit();
    },
    onError: apiError
  });

  const handleChange = ({ target: { value }}: SelectChangeEvent): void => {
    setSelectedEntity(value);
  };

  const handleSubmit = () => {
    // An empty selection means account level (no entity); the backend treats an
    // empty new_entity_name as clearing the entity, so always submit.
    assignEntity.mutate({
      iam_user_id: iamUserId,
      new_entity_name: selectedEntity
    });
  };

  if (isLoading) return <Loading />

  return (
    <Box pt={2}>
      <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
        <FormControl fullWidth>
          <InputLabel id="entity-names-label">Entity Names</InputLabel>
          <Select
            labelId="entity-names-label"
            value={selectedEntity}
            label="Entity Names"
            onChange={handleChange}
            displayEmpty
            renderValue={(selected) => (selected ? (selected as string) : 'Account Level')}
          >
            <MenuItem value="">Account Level</MenuItem>
            { entities && entities.map((item) => {
              return <MenuItem value={item} key={item}>{item}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </Box>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleSubmit}>
          Assign
        </Button>
      </Box>
    </Box>
  );
};
